
import React from 'react';
import { Link } from 'react-router-dom';
import { RefreshCw, Home, AlertTriangle, Phone, Mail } from 'lucide-react';

interface ServerErrorProps {
  errorCode?: number;
  message?: string;
  onRetry?: () => void;
}

const ServerError: React.FC<ServerErrorProps> = ({
  errorCode = 500,
  message = "An internal server error occurred",
  onRetry
}) => {
  const handleRetry = () => {
    if (onRetry) {
      onRetry();
    } else {
      window.location.reload();
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <div className="max-w-lg w-full text-center">
        {/* Animated Error Icon */}
        <div className="relative mb-8">
          <div className="w-32 h-32 mx-auto bg-destructive rounded-full flex items-center justify-center shadow-2xl">
            <AlertTriangle className="w-16 h-16 text-destructive-foreground animate-pulse" />
          </div>
          <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-32 h-32 bg-destructive/20 rounded-full -z-10 animate-ping opacity-20"></div>
        </div>

        {/* Error Content */}
        <div className="space-y-6">
          <div>
            <h1 className="text-8xl font-bold text-destructive mb-4">
              {errorCode}
            </h1>
            <h2 className="text-3xl font-bold text-foreground mb-2">
              Server error
            </h2>
            <p className="text-lg text-muted-foreground mb-8">
              {message}. Our technical teams have been notified.
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={handleRetry}
              className="inline-flex items-center justify-center space-x-2 bg-destructive text-destructive-foreground px-6 py-3 rounded-lg font-medium hover:opacity-90 transition-all duration-200 transform hover:scale-105 shadow-lg"
            >
              <RefreshCw size={20} />
              <span>Retry</span>
            </button>

            <Link
              to="/user/dashboard"
              className="inline-flex items-center justify-center space-x-2 bg-card text-card-foreground px-6 py-3 rounded-lg font-medium border border-border hover:bg-muted transition-all duration-200 transform hover:scale-105 shadow-lg"
            >
              <Home size={20} />
              <span>Back to home</span>
            </Link>
          </div>

          {/* Error Details */}
          <div className="mt-8 p-6 bg-card rounded-2xl shadow-lg border border-border">
            <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center justify-center space-x-2">
              <AlertTriangle className="w-5 h-5 text-destructive" />
              <span>Error details</span>
            </h3>

            <div className="space-y-3 text-sm text-muted-foreground">
              <div className="flex justify-between">
                <span className="font-medium">Error code:</span>
                <span className="font-mono bg-muted px-2 py-1 rounded">{errorCode}</span>
              </div>
              <div className="flex justify-between">
                <span className="font-medium">Time:</span>
                <span>{new Date().toLocaleString('en-US')}</span>
              </div>
              <div className="flex justify-between">
                <span className="font-medium">Session ID:</span>
                <span className="font-mono bg-muted px-2 py-1 rounded">
                  {Math.random().toString(36).substring(2, 10).toUpperCase()}
                </span>
              </div>
            </div>
          </div>

          {/* Support Contact */}
          <div className="mt-8 p-4 bg-muted rounded-xl border border-border">
            <div className="flex items-center justify-center space-x-2 mb-2">
              <Phone className="w-5 h-5 text-primary" />
              <h4 className="font-semibold text-foreground">Need help?</h4>
            </div>
            <p className="text-sm text-muted-foreground mb-3">
              If the problem persists, contact our technical support.
            </p>
            <a
              href="mailto:contact@growth-energy.fr"
              className="inline-flex items-center space-x-2 text-primary hover:opacity-80 font-medium transition-colors"
            >
              <Mail size={16} />
              <span>contact@growth-energy.fr</span>
            </a>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-12 text-sm text-muted-foreground">
          <p>EMA - Growth Energy - Technical support available 24/7</p>
        </div>
      </div>
    </div>
  );
};

export default ServerError;
